import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../Elements/Button/Button";
import RestaurantGrid from "../../Sections/RestaurantHome/RestaurantGrid/RestaurantGrid";
import { fetchRestaurants } from "../../../Constants/getRestaurants";
import useGeolocation from "../../../Hooks/useGeolocation";
import {
  isValidCoord,
  annotateWithDistance,
  filterByRadius,
  RADIUS_KM
} from "../../../Utilities/geocode";
import Loader from "../../Elements/Loader/Loader";
import "./buscador.css";

const Buscador = () => {
  const [query, setQuery] = useState("");
  const [baseList, setBaseList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { status: geoStatus, coords } = useGeolocation();
  const navigate = useNavigate();

  // 1) Cargar una sola vez y dejar lista la base de datos local (≤ 5 km)
  useEffect(() => {
    if (geoStatus !== "granted" || !coords) return;

    (async () => {
      try {
        setLoading(true);
        setError("");
        const all = await fetchRestaurants(500);

        const valid = (all || []).filter((r) => isValidCoord(r.lat, r.lng));
        const withDist = annotateWithDistance(coords, valid);
        const inRadius = filterByRadius(withDist, RADIUS_KM)
          .sort((a, b) => (a.distanceKm || 0) - (b.distanceKm || 0));

        setBaseList(inRadius);
      } catch (err) {
        setError(err?.message || "Error al cargar restaurantes cercanos");
      } finally {
        setLoading(false);
      }
    })();
  }, [geoStatus, coords]);

  // 2) SOLO mostramos resultados si hay texto
  const results = useMemo(() => {
    const text = query.trim().toLowerCase();
    if (!text) return []; // 👈 clave: sin texto, no mostramos nada

    const match = (item) => {
      const categoriaPrincipal = item.categoriaPrincipal?.toLowerCase() || "";
      const categorias = (item.categorias || []).map((c) => c.toLowerCase());
      const name = item.name?.toLowerCase() || "";
      const productos = (item.productos || []).map(
        (p) =>
          `${p.name?.toLowerCase() || ""} ${p.descripcion?.toLowerCase() || ""}`
      );

      return (
        categoriaPrincipal.includes(text) ||
        categorias.some((c) => c.includes(text)) ||
        name.includes(text) ||
        productos.some((p) => p.includes(text))
      );
    };

    return baseList.filter(match);
  }, [baseList, query]);

  const goToDetail = (id) => id && navigate(`/restaurants/${id}`);

  // 3) Estados de geolocalización
  if (geoStatus === "pending") {
    return (
      <section className="buscador">
        <div className="container-fluid py-3 py-md-4">
          <h2 className="home-h2">¿Qué necesitas el día de hoy?</h2>
          <p className="muted">Solicitando tu ubicación…</p>
        </div>
      </section>
    );
  }

  if (geoStatus !== "granted") {
    return (
      <section className="buscador">
        <div className="container-fluid py-3 py-md-4">
          <h2 className="home-h2">¿Qué necesitas el día de hoy?</h2>
          <p className="error">
            Para usar el buscador es necesario habilitar tu ubicación. Solo
            mostraremos comercios a ≤ {RADIUS_KM} km.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="buscador">
      <div className="container-fluid py-3 py-md-4">
        <h2 className="home-h2">
          ¿Qué necesitas el día de hoy? <span className="muted">(≤ {RADIUS_KM} km)</span>
        </h2>

        <div className="buscador-form">
          <input
            type="text"
            placeholder="Buscar por nombre, categoría o producto..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button text="Buscar" color="blue" fill onClick={() => {/* opcional */ }} />
        </div>

        {loading && <Loader />}
        {error && <p className="error">{error}</p>}

        {/* 👇 Solo aparece el grid si el usuario escribió algo */}
        {!loading && !error && results.length > 0 && query.trim() && (
          <RestaurantGrid items={results} onOpenDetail={goToDetail} />
        )}

        {/* Mensaje cuando escribió pero no hay matches */}
        {!loading && !error && results.length === 0 && query.trim() && (
          <p className="muted">
            No se encontraron resultados para “{query}” dentro de {RADIUS_KM} km.
          </p>
        )}
      </div>
    </section>
  );
};

export default Buscador;